<template>
    <div class="Block Dashboard Reset">
        <Logo />

        <div class="inner">
            <div class="Heading">
                <h1 class="h1">{{ trans('Wachtwoord wijzigen') }}</h1>
            </div>
            <div class="Middle">
                <form ref="formRef" class="cs_form" @submit.prevent="handleSubmit">
                    <div class="row">
                        <div class="col-sm-6">
                            <Fields
                                v-if="componentData.form"
                                :form="form"
                                :fields="componentData.form"
                                :values="{}"
                                :show_label=false
                                :show_placeholder=true
                                :show_error_message=true
                                :translate="true"
                            />
                        </div>
                        <div class="col-sm-6">
                            <div class="element TextField">
                                <label for="password-strength-meter">{{ trans('Veiligheid wachtwoord') }}</label>
                                <div class="progress" style="height:10px;">
                                    <div id="password-strength-meter"
                                         class="progress-bar"
                                         :style="{ width: strengthPercent + '%' }"
                                         :class="strengthClass"
                                         role="progressbar"
                                         :aria-valuenow="strengthPercent"
                                         aria-valuemin="0"
                                         aria-valuemax="100">
                                    </div>
                                </div>
                                <p>{{ trans('Een veilig wachtwoord bestaat uit:') }}</p>
                                <ul>
                                    <li>{{ trans('Kleine letters én hoofdletters') }}</li>
                                    <li>{{ trans('Speciale tekens') }}</li>
                                    <li>{{ trans('Ten minste 8 karakters') }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <MessageOverview ref="messageRef" />
                    <nav>
                        <button type="submit" class="more">
                            <span class="title">{{ trans('Wachtwoord wijzigen') }}</span>
                        </button>
                    </nav>
                    <nav>
                        <button type="button" class="more-inline" @click="backToLogin(isCommunity)">
                            <span class="title">{{ trans('Terug naar login') }}</span>
                        </button>
                    </nav>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import {defineComponent, ref, reactive, watch, computed, inject } from "vue";

import { useRouter } from "vue-router";

import { apiHostname } from "config";

import { fetchToken } from "services/JwtService";

import Fields from "formbuilder/fields.vue";
import { useFormValidation } from "formbuilder/fields/_validation";

import { isPortal } from "config/router";
import { getPasswordResetForm } from "config/forms";

import Icon from "components/icon.vue";

import MessageOverview from "elements/message.vue";
import ButtonText from "elements/button-text";

// Our helpers
import trans from "helpers/_translation";
import { postData } from "helpers/_api";

import Logo from "layout/general/logo";
import LanguageSelect from "elements/language-select";

export default defineComponent({
    name: "dashboardReset",
    props: {
        boxed: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    components: {
        Logo,
        Fields,
        Icon,
        MessageOverview,
        ButtonText,
        LanguageSelect
    },
    setup(props) {
        const router = useRouter();

        const isCommunity = inject("isCommunity");

        const messageRef = ref();
        const formRef = ref();
        const componentSettings = reactive({
            use_label: false,
            use_placeholder: true
        });

        // Our component behaviour
        const componentData = reactive({
            form: []
        });

        // Functions and helpers
        const backToLogin = (isCommunity) => {
            if (isCommunity) {
                router.push('/portal');
                return;
            }
            router.push('/login');
        };

        const getAuthToken = () => {
            // Huidige URL parameters ophalen
            const params = new URLSearchParams(window.location.search);

            return params.get('auth_token');
        };

        const handleSubmit = (e) => {
            messageRef.value.resetMessages();
            form.handleSubmit().then(() => {
                const formData = new FormData(formRef.value);
                if (formData.get('new_password') !== formData.get('confirm_password')) {
                    messageRef.value.setMessage(
                        "Beide nieuwe wachtwoorden zijn niet gelijk.",
                        "error"
                    );
                    return;
                }

                const authToken = getAuthToken();
                strength.value = calculatePasswordStrength(formData.get('new_password'));
                if (strength.value < 3) {
                    messageRef.value.setMessage(
                        "Het wachtwoord is niet sterk genoeg.",
                        "error"
                    );
                    return;
                }
                postData(`${apiHostname}/api/v1.0/user/1/ChangePassword?auth_token=${authToken}`, formData).then(
                    () => {
                        // And then go to login screen
                        messageRef.value.setMessage("Uw inloggegevens zijn verstuurd.", "message");
                        backToLogin(isCommunity);
                    },
                    (json) => {
                        console.log('json', json);
                        if (json.status === 401) {
                            messageRef.value.setMessage("Token is niet bekend.");
                            return;
                        }
                        if (json.status === 403) {
                            messageRef.value.setMessage(
                                "Het token is na 15 minuten verlopen. " +
                                "Vraag een nieuwe wachtwoordlink aan en stel het nieuwe wachtwoord binnen die tijd in."
                            );
                            return;
                        }
                        if (json.status === 404) {
                            messageRef.value.setMessage("Gebruiker is niet bekend.", "error");
                            return;
                        }
                        if (json.status === 400) {
                            const errorMessages = {
                                'invalid-current-password': 'Het ingevoerde huidige wachtwoord is ongeldig.',
                                'generic-login-error': 'Het wachtwoord van ibizz medewerkers kan via hier niet worden gewijzigd.',
                                'password-change-error': 'Het wachtwoord kon niet worden aangepast.'
                            };

                            let messages = [];
                            if (json?.errors && Array.isArray(json.errors) && json.errors.length > 0) {
                                json.errors.forEach((error) => {
                                    messages.push(
                                        errorMessages[error?.type] ||
                                        error?.message ||
                                        'Er ging iets fout. Probeer het later nog eens.'
                                    );
                                });
                            }

                            if (messages.length === 0) {
                                messages.push('Er ging iets fout. Probeer het later nog eens.');
                            }

                            messages.forEach((message) => {
                                messageRef.value.setMessage(message);
                            });
                            return;
                        }
                        console.error("Error with validating the handle as Register form", json.status);
                        messageRef.value.setMessage("Er ging iets fout. Probeer het later nog eens.", "error");
                    }
                );
            });
        };

        const form = useFormValidation();
        componentData.form = getPasswordResetForm();

        const strength = ref(0);
        const strengthPercent = computed(() => strength.value * 20); // 0–100%
        const strengthClass = computed(() => {
            return {
                "progress-bar-danger":  strength.value < 1,
                "progress-bar-warning": strength.value < 2,
                "progress-bar-info":    strength.value < 3,
                "progress-bar-success": strength.value === 3,
            }
        });

        // password strength functie
        const calculatePasswordStrength = function (value = "") {
            let score = 0;

            // 1. Kleine letters én hoofdletters
            if (/[a-z]/.test(value) && /[A-Z]/.test(value)) {
                score++;
            }

            // 2. Speciale tekens
            if (/[^a-zA-Z0-9]/.test(value)) {
                score++;
            }

            // 3. Minstens 8 karakters
            if (value.length >= 8) {
                score++;
            }

            return score; // 0 – 3
        };

        return {
            form,

            messageRef,
            trans,
            formRef,
            componentData,
            componentSettings,
            isCommunity,
            strength,
            strengthPercent,
            strengthClass,

            handleSubmit,
            backToLogin
        }
    },
});
</script>

<style>
</style>